import { ConnectionOptions, GraphApiMethod } from '@/api/core';
import {
  DispatchSummaryCallVolumeEnum,
  DispatchSummaryWithCache,
  DispatchSummaryWithCacheSearch,
  DownloadDispatchSummaryWithCacheAsCsvResponse,
  QueryDownloadDispatchSummaryWithCacheAsCsvArgs,
} from '@/models/gen/graphql';
import createGraphApiHook, { CreateGraphApiHookHook } from '@/hooks/createGraphApiHook';

import { TypedDocumentNode } from '@apollo/client';
import { getProperty, queryInput, QueryInputType } from '@/utils';
import graphApi from '@/api';
import { DownloadDispatchSummaryWithCacheAsCsvDocument } from '@/api/queries';

// Service Function Signature
type ServiceMethodPayload = ({
  startDatetime?: string;
  endDatetime?: string;
  callVolumes?: DispatchSummaryCallVolumeEnum[];
  dispatcherStations?: string[];
} & {
  [key in keyof DispatchSummaryWithCacheSearch]?: any[];
})[];
interface ServiceMethod {
  (payload?: ServiceMethodPayload, options?: ConnectionOptions): Promise<DownloadDispatchSummaryWithCacheAsCsvResponse>;
}

const dispatchSummaryCSVKeys: (keyof DispatchSummaryWithCache)[] = [
  'callVolume',
  'commentsFaxallCheck',
  'commentsRepeatDistractedOffenders',
  'commentsRepeatSpeedingOffenders',
  'dispatcherAssetCamerasWorking',
  'dispatcherAssetCamerasWorkingNotes',
  'dispatcherDayOverview',
  'dispatcherEightByEightIssue',
  'dispatcherFirstName',
  'dispatcherLastName',
  'dispatcherPassdown',
  'dispatcherServiceIssues',
  'dispatcherShift',
  'dispatcherSoftwareIssues',
  'dispatcherStationVansIssues',
  'manifestsDrivingIssues',
  'manifestsTaxis',
  'manifestsTrainingIssues',
];

const dispatchSummaryCSVColumns: Record<keyof DispatchSummaryWithCache, true> = Object.fromEntries(
  dispatchSummaryCSVKeys.map((key) => [key, true])
) as Record<keyof DispatchSummaryWithCache, true>;

// DispatchSummary CSV Service
export const { downloadDispatchSummaryWithCacheCsv, useDownloadDispatchSummaryWithCacheCsv } = ((): {
  downloadDispatchSummaryWithCacheCsv: ServiceMethod;
  useDownloadDispatchSummaryWithCacheCsv: CreateGraphApiHookHook<ServiceMethod>;
} => {
  const [fetch] = graphApi(DownloadDispatchSummaryWithCacheAsCsvDocument).map(
    (fn: GraphApiMethod<TypedDocumentNode>): ServiceMethod =>
      async (payload?: ServiceMethodPayload, options?: ConnectionOptions): Promise<DownloadDispatchSummaryWithCacheAsCsvResponse> => {
        const query: DispatchSummaryWithCacheSearch[] = payload?.map((item) => ({
          createdAt: queryInput([item?.startDatetime, item?.endDatetime], QueryInputType.RANGE),
          callVolume: queryInput.orNull(item?.callVolumes),
          dispatcherStation: queryInput.orNull(item?.dispatcherStations),
        }));
        const args: QueryDownloadDispatchSummaryWithCacheAsCsvArgs = {
          input: {
            query,
            columns: dispatchSummaryCSVColumns,
          },
        };
        const opts = { merge: options?.merge ? 'downloadDispatchSummaryWithCacheAsCSV' : undefined };
        const res = await fn(args, opts);
        return getProperty('downloadDispatchSummaryWithCacheAsCSV', res);
      }
  );
  const useService = createGraphApiHook(fetch);
  return {
    downloadDispatchSummaryWithCacheCsv: fetch,
    useDownloadDispatchSummaryWithCacheCsv: useService,
  };
})();
